/* eslint-disable no-cond-assign */

import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import { DataPoints, DataPointResponse } from '../DataPoints';
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { REGIONS, ASSET_ALLOCATION_GROUPS } from '../../../../utils/constants';
import { getCountryName } from '../../../../utils/i18n';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';
import { PercentageCell } from '../../../../views/components/cells/index';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import { PROPOSAL_DISCLAIMERS } from '../../utils/constants';
import Dictionary from '@/types/dictionary';
import DonutData from '@/types/donut-data';

/**
 * @class AssetAllocation Data Access Object
 */
export default class EquityRegionAllocationDAO extends ComparisonItemBasedProposalDAO {
  countryExclusionMap: Dictionary<string[]> = {};

  dataKeys: string[] = Object.values(REGIONS);

  countryKeys: string[] = [];

  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new EquityRegionAllocationDAO(proposal, proposalElement);
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return ['ASSET_ALLOCATION_COLORS', 'RISK_ALLOCATION_COLORS'];
  }

  // eslint-disable-next-line class-methods-use-this
  private hasEmptyRegions(itemDAO: ComparisonItemDAO) {
    const equityAllocation = DataPoints.getAssetAllocationTotalsByCategory(itemDAO.item, ASSET_ALLOCATION_GROUPS.EQUITY).response;
    return equityAllocation > 0 && itemDAO.item.allocations && itemDAO.item.allocations.equityRegion && itemDAO.item.allocations.equityRegion.length === 0;
  }

  protected build(): void {
    // test for options
    const daoConfig: ProposalNS.EquityRegionAllocationConfig = this.proposalElement.DAO as ProposalNS.EquityRegionAllocationConfig;
    if (daoConfig.regionExclusionConfigDAOOptions !== undefined) {
      this.countryExclusionMap = daoConfig.regionExclusionConfigDAOOptions.countryExclusions;
      for (const values of Object.values(this.countryExclusionMap)) {
        this.countryKeys.push(...values);
      }
    }

    let itemDAO: ComparisonItemDAO | null = null;
    while ((itemDAO = this.nextItem()) !== null) {
      const keys: string[] = DataPoints.getEquityRegionAllocationsKeys(itemDAO.item);
      for (const key of keys) {
        let fieldName = this.dataKeys.includes(key) ? this.formatMessage(`Comparison.Table.FixedIncomeRegions.${key}`) : getCountryName(key, this.locale);
        const dpResponse: DataPointResponse<number> = DataPoints.getEquityRegionWithCountry(itemDAO.item, key, this.countryExclusionMap);
        if (this.hasEmptyRegions(itemDAO) && key.toUpperCase() === ASSET_ALLOCATION_GROUPS.OTHER) {
          dpResponse.response = 100;
          this.addDisclaimerKeys([PROPOSAL_DISCLAIMERS.ROLL_INTO_OTHER], itemDAO.item);
          fieldName += '*';
        }
        this.addDisclaimerKeys(dpResponse.disclaimerKeys, itemDAO.item);

        this.addFieldName(fieldName, ProposalStyleClassNames.ProposalTableContent);
        const value: number = dpResponse.response ? dpResponse.response : 0;
        const field = new ProposalFieldDAO(fieldName, value, ProposalStyleClassNames.ProposalTableContent);
        field.cellType = PercentageCell;
        field.key = key;
        this.addFieldDAO(itemDAO, field);
      }
    }
  }

  getDonutData(index: number = 0): DonutData | null {
    const out: DonutData | null = super.getDonutData(index);

    if (out && this.colorsDict) {
      out.colorsDict = this.colorsDict;
    }

    return out;
  }
}
